// src/utils/viewFilters.ts
//
// Per-view filter persistence for the ticket desk. Filters persist "per view
// per session" (Story 4.1 AC 2 / Decision 9): survive navigation, die with the
// tab session → sessionStorage, keyed `yak.ticketFilters.<view>`. Page number
// is deliberately NOT persisted (returning to a view restarts at page 1).
//
// Robustness: malformed stored JSON (or an unavailable sessionStorage, e.g.
// private-mode quirks) degrades silently to defaults and never throws — a
// corrupt filter blob must not brick the desk.

import type { YakTicketViewKey, YakTicketFilters } from '../types/yak'

export const EMPTY_TICKET_FILTERS: YakTicketFilters = {
  questionType: null,
  brand: null,
  routeLabel: null,
  age: null,
  reference: null,
}

function storageKey(view: YakTicketViewKey): string {
  return `yak.ticketFilters.${view}`
}

// Only the five known keys are read back, each coerced to string-or-null, so a
// tampered/oversized blob can't inject unexpected fields into a query.
function coerce(raw: unknown): YakTicketFilters {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_TICKET_FILTERS }
  const r = raw as Record<string, unknown>
  const str = (v: unknown): string | null => (typeof v === 'string' ? v : null)
  return {
    questionType: str(r.questionType) as YakTicketFilters['questionType'],
    brand: str(r.brand),
    routeLabel: str(r.routeLabel) as YakTicketFilters['routeLabel'],
    age: str(r.age) as YakTicketFilters['age'],
    reference: str(r.reference),
  }
}

export function loadFilters(view: YakTicketViewKey): YakTicketFilters {
  try {
    const stored = sessionStorage.getItem(storageKey(view))
    if (!stored) return { ...EMPTY_TICKET_FILTERS }
    return coerce(JSON.parse(stored))
  } catch {
    return { ...EMPTY_TICKET_FILTERS }
  }
}

export function saveFilters(
  view: YakTicketViewKey,
  filters: YakTicketFilters
): void {
  try {
    sessionStorage.setItem(storageKey(view), JSON.stringify(filters))
  } catch {
    // A failed write (quota, disabled storage) is non-fatal — the live filter
    // state still drives the current session; only cross-navigation memory is
    // lost. Never surface this to the user.
  }
}

export function hasActiveFilters(filters: YakTicketFilters): boolean {
  return (
    filters.questionType !== null ||
    filters.brand !== null ||
    filters.routeLabel !== null ||
    filters.age !== null ||
    // A whitespace-only reference is not an active filter (the service ignores
    // it too) — "Clear filters" must not appear for an empty text box.
    (filters.reference !== null && filters.reference.trim() !== '')
  )
}
